import React from 'react';

import { ButtonTag } from '@/components/shared/buttons/button-tag';
import { Link } from '@/components/shared/element/link';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { Flex } from '@/components/shared/layout/flex';
import { Grid } from '@/components/shared/layout/grid';
import { Image } from '@/components/shared/media/image';
import { generateMobileAndLargeMobileImageSources } from '@/components/shared/media/image/generate-sources';
import { formatDateFull } from '@/components/shared/utility/format';
import { MENU_LCV_ABOUT_NEWS } from '@/contents/lcv/link';
import { INewsItem } from '@/types/news/news';

interface NewsItemProps extends INewsItem {
  dataTest?: string;
  dataTrack?: string;
}

const NewsItem: React.FC<NewsItemProps> = ({
  dataTrack = 'news-list',
  dataTest = 'newsList',
  ...props
}) => {
  return (
    <Box
      component="article"
      test={{
        dataTest: dataTest,
      }}
    >
      <Grid alignItems="start" rowGap="4">
        {!!props.image && !!props.image.src && (
          <Link
            href={props.url}
            tracking={{
              dataTrack: dataTrack,
              dataTrackText: props.title,
              dataTrackUrl: props.url,
            }}
            test={{
              dataTest: `${dataTest}_link`,
            }}
          >
            <Image
              placeholder="shimmer"
              hoverEffect="zoom"
              src={props.image.src}
              alt={props.image.alt}
              width={600}
              height={338}
              layout="responsive"
              aspectRatio="16by9"
              sources={[
                {
                  srcset: props.image.src,
                  media: { minWidth: 768 },
                  options: { width: 414, crop: '16:9' },
                },
                ...generateMobileAndLargeMobileImageSources({
                  mobileSrc: props.image.src,
                  mobileOptions: {
                    crop: '16:9',
                  },
                }),
              ]}
            />
          </Link>
        )}
        {!!props.title && !!props.beginDate && (
          <Grid rowGap="3">
            {!!props.beginDate && (
              <Flex alignItems="center" columnGap="2">
                {!!props.category?.url && !!props.category.name && (
                  <ButtonTag
                    label={props.category.name}
                    href={`${MENU_LCV_ABOUT_NEWS.url}/${props.category.url}`}
                  />
                )}
                {!!props.beginDate && (
                  <Text
                    as="p"
                    size="sm"
                    variant="text-gray"
                    data-test={`${dataTest}_beginDate`}
                    css={{ marginBottom: -2 }}
                  >
                    {formatDateFull(props.beginDate)}
                  </Text>
                )}
              </Flex>
            )}
            {!!props.title && (
              <Link
                href={props.url}
                tracking={{
                  dataTrack: dataTrack,
                  dataTrackText: props.title,
                  dataTrackUrl: props.url,
                }}
                test={{
                  dataTest: `${dataTest}_link`,
                }}
                hoverColor="red"
              >
                <Text
                  as="h3"
                  size="text-body-1"
                  font="bold"
                  lines="3"
                  wrap
                  dangerouslySetInnerHTML={{
                    __html: props.title,
                  }}
                  data-test={`${dataTest}_title`}
                />
              </Link>
            )}
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
NewsItem.displayName = 'NewsItem';
export default NewsItem;
